import React, { useContext, useEffect, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import { Navigate } from '@lib/react-router-dom'

import { BlockedPage } from '@pages/Blocked/Blocked.page'
import { CompromisedPassword } from '@pages/CompromisedPassword/CompromisedPassword'
import { useDashboardRoutes } from '@pages/Dashboard/DashboardRoutes'
import { ForgotPasswordPage } from '@pages/ForgotPassword/ForgotPassword.page'
import { ResetPasswordPage } from '@pages/ForgotPassword/ResetPassword.page'
import { JourneyPage } from '@pages/Journey/JourneyPage'
import { LoginPage } from '@pages/Login/Login.page'
import { LogoutPage } from '@pages/Logout/LogoutPage'
import MarketingPage from '@pages/MarketingForm/MarketingPage'
import { TwoFactorAuthConfirmPage } from '@pages/Profile/AccountSettings/SecuritySettings/TwoFactor/TwoFactorAuthConfirm.page'
import { useProfileRoutes } from '@pages/Profile/ProfileRoutes'
import { ContinueRegistrationPage } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsStep4/ContinueRegistrationPage'
import { VerifyEmailRedirect } from '@pages/Signup/Pages/PersonalDetails/PersonalDetailsStep4/VerifyEmailPage'
import { useSignupRoutes } from '@pages/Signup/routes/useSignupRoutes'
import { TwoFactorAuthErrorPage } from '@pages/TwoFactorAuth/TwoFactorAuthErrorPage'
import { TwoFactorAuthPage } from '@pages/TwoFactorAuth/TwoFactorAuthPage'
import { WrongEntityPage } from '@pages/WrongEntity/WrongEntityPage'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { CustomerSupportProvider } from '@context/CustomerSupportContext'
import { useMaintenance } from '@context/MaintenanceModeContext'
import { useProductReadContext } from '@context/ProductContext'
import {
  usePublicDomainsReadContext,
  usePublicDomainsWriteContext,
} from '@context/PublicDomainsContext'

import { useAccountAccessStatuses } from '@utils/AccountAccess/accountAccessStatuses'
import {
  hasConsentedToLegalDocuments,
  isAccountDocumentManagementPermitted,
} from '@utils/AccountAccess/accountDocumentStatuses'
import {
  isAccountKycUpdateDetailsNotAllowed,
  isAccountKycUpdateDetailsRequired,
  isDepositBeforeKYC,
  isSumsubNeeded,
} from '@utils/AccountAccess/accountKYCStatuses'
import {
  hasSubmittedAppropriatenessTest,
  isAccountAppropriatenessTestAllow,
  isEconomicProfileTestNeeded,
  isProductAppropriatenessInvalid,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { useAccountVisibilityStatuses } from '@utils/AccountAccess/accountVisibilityStatuses'
import { isTickmillUK } from '@utils/companyName.utils'
import {
  BasePaths,
  CommonPaths,
  getProductAppTestRiskAcceptancePath,
  getProductEconomicProfilePath,
} from '@utils/path'

import { isAccountDormantUpdateNeeded, isVerificationExpedited } from '@model/AccountDetailedDto'

import { RedirectionManager } from '@global/RedirectionManager/RedirectionManager'
import { WalletSetupLoader } from '@global/WalletSetupLoader/WalletSetupLoader'
import { Locale } from '@global/locale/Locale'
import { PageNotFound } from '@global/notFound/PageNotFound'
import { SumsubThankYouPage } from '@global/sumsub/SumsubThankYouPage'

import { useDocumentVerificationPath } from '@hooks/useDocumentVerificationPath'

import { Loading } from '@shared/components/Loading/Loading'

import { useAmplitudeJournies } from '@analytics/amplitude'

import { LayoutResolver } from './LayoutResolver'
import { ProductRedirect } from './ProductRedirect'
import { useRenderRoutes } from './RenderRoutes'
import { TradingAccountTradeRedirect } from './TradingAccountTradeRedirect'

export const MainContainer: React.FC<{
  urlLocale?: Locale
  setCurrentPath: (path: string) => void
}> = (props) => {
  const { setCurrentPath } = props
  const [auth] = useContext(AuthSessionContext)
  const { account } = useAccountReadContext()
  const { isMaintenanceMode } = useMaintenance()
  const [isActivated, setIsActivated] = useState(true)
  useAmplitudeJournies()

  const renderRoutes = useRenderRoutes()
  const DashboardRoutes = useDashboardRoutes()
  const ProfileRoutes = useProfileRoutes()
  const SignupRoutes = useSignupRoutes()

  const { clearAccount } = useAccountWriteContext()

  const location = useLocation()
  const { search } = location
  const { refreshDomains } = usePublicDomainsWriteContext()
  const { domains } = usePublicDomainsReadContext()

  const { product } = useProductReadContext()
  const accountAccessStatuses = useAccountAccessStatuses()
  const accountVisibilityStatuses = useAccountVisibilityStatuses()
  const docVerificationPath = useDocumentVerificationPath()

  const hasRedirQuery = new URLSearchParams(location.search).get('redir') !== null

  useEffect(() => {
    if (!auth) {
      clearAccount()
    }
    if (!account) {
      return
    }
    setIsActivated(accountAccessStatuses.isAccountActivatedStatus)

    if (accountVisibilityStatuses.isAccountVisibilityLockUnlockStatus) {
      setIsActivated(true)
    }
    if (!domains) {
      refreshDomains()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, product, auth, domains])

  if (isMaintenanceMode) {
    return (
      <Routes>
        <Route element={<LayoutResolver setCurrentPath={setCurrentPath} />}>
          <Route path={'/login'} element={<LoginPage />} />
          <Route path={'/*'} element={<Navigate to={'/login'} />} />
        </Route>
      </Routes>
    )
  }

  if (auth && !account) {
    return <Loading isLoading showLoadingIcon />
  }

  if (auth && account) {
    return (
      <CustomerSupportProvider>
        <Routes>
          <Route path='/trading-account-trade-redirect' element={<TradingAccountTradeRedirect />} />
          <Route path='/logout' element={<LogoutPage />} />
          <Route
            element={
              <LayoutResolver routeConfigs={DashboardRoutes} setCurrentPath={setCurrentPath} />
            }
          >
            <Route path={CommonPaths.PasswordCompromised} element={<CompromisedPassword />} />
            {isActivated && hasRedirQuery && (
              <>
                <Route path='/' element={<RedirectionManager />} />
                <Route path='/login' element={<RedirectionManager />} />
              </>
            )}

            {!isAccountAppropriatenessTestAllow(account, product) &&
              !isEconomicProfileTestNeeded(account, product) &&
              !showApptestRiskScreen(account, product) &&
              isAccountDormantUpdateNeeded(account) &&
              ['dashboard/:tab/*', 'profile/:tab/*'].map((path) => (
                <Route
                  key={`kyc-needed-${path}`}
                  path={path}
                  element={<Navigate to={CommonPaths.KycUpdateDetails} />}
                />
              ))}
            {isEconomicProfileTestNeeded(account, product) &&
              (!isProductAppropriatenessInvalid(product, account) ||
                showApptestRiskScreen(account, product)) &&
              [CommonPaths.DocumentManagement, CommonPaths.Sumsub, BasePaths.TakeAppTest].map(
                (path) => (
                  <Route
                    key={`economic-profile-test-${path}`}
                    path={path}
                    element={
                      <Navigate
                        to={
                          showApptestRiskScreen(account, product)
                            ? getProductAppTestRiskAcceptancePath(product)
                            : getProductEconomicProfilePath(product)
                        }
                      />
                    }
                  />
                )
              )}
            {isAccountDocumentManagementPermitted(product, account) &&
              !isAccountKycUpdateDetailsRequired(account, product, true) &&
              ['/dashboard', BasePaths.TakeAppTest, CommonPaths.KycUpdateDetails].map((path) => (
                <Route
                  key={`doc-needed-${path}`}
                  path={path}
                  element={<Navigate to={docVerificationPath} />}
                />
              ))}
            {!hasConsentedToLegalDocuments(account, product) &&
              [CommonPaths.DocumentManagement, CommonPaths.Sumsub].map((path) => (
                <Route
                  key={`product-legal-docs-${path}`}
                  path={path}
                  element={<Navigate to={CommonPaths.ProductRegistrationLegalDocs} />}
                />
              ))}
            {isProductAppropriatenessInvalid(product, account) &&
              (hasSubmittedAppropriatenessTest(account, product) || !isTickmillUK(account)) &&
              [CommonPaths.DocumentManagement, CommonPaths.Sumsub].map((path) => (
                <Route
                  key={`apptest-needed-${path}`}
                  path={path}
                  element={<Navigate to={BasePaths.TakeAppTest} />}
                />
              ))}
            {isProductAppropriatenessInvalid(product, account) &&
              [CommonPaths.DocumentManagement, CommonPaths.Sumsub].map((path) => (
                <Route
                  key={`take-apptest-${path}`}
                  path={path}
                  element={<Navigate to={BasePaths.TakeAppTest} />}
                />
              ))}

            {isProductAppropriatenessInvalid(product, account) &&
              [
                '/dashboard/traders-room',
                '/dashboard/introducing-broker',
                '/dashboard/payment-agent',
                CommonPaths.KycUpdateDetails,
              ].map((path) => (
                <Route
                  key={`product-test-${path}`}
                  path={path}
                  element={<Navigate to={BasePaths.TakeAppTest} />}
                />
              ))}

            {(isDepositBeforeKYC(account) || isVerificationExpedited(account)) && (
              <Route path={CommonPaths.AccountLoading} element={<WalletSetupLoader />} />
            )}

            {isSumsubNeeded(account) && (
              <>
                <Route path={CommonPaths.AccountLoading} element={<WalletSetupLoader />} />
                <Route path={CommonPaths.AccountPending} element={<SumsubThankYouPage />} />
                {/*<Route*/}
                {/*  path={CommonPaths.AccountNewPassword}*/}
                {/*  element={<CreateTradingAccountPasswordPage />}*/}
                {/*/>*/}
              </>
            )}

            {account && isAccountKycUpdateDetailsNotAllowed(account, product) && (
              <Route
                path={CommonPaths.KycUpdateDetails}
                element={<Navigate to='/profile/personal-info' />}
              />
            )}

            {renderRoutes(DashboardRoutes)}
            {renderRoutes(ProfileRoutes)}
            {renderRoutes(SignupRoutes)}

            <Route path='/journey' element={<JourneyPage />} />
            <Route path='/wrong-entity' element={<WrongEntityPage />} />
            <Route path='/404' element={<PageNotFound />} />
            <Route path='*' element={<ProductRedirect />} />
          </Route>
          <Route element={<LayoutResolver setCurrentPath={setCurrentPath} />}>
            <Route path='/2fa/confirm' element={<TwoFactorAuthConfirmPage />} />
          </Route>
        </Routes>
      </CustomerSupportProvider>
    )
  }

  if (!auth) {
    return (
      <Routes>
        <Route path='/trading-account-trade-redirect' element={<TradingAccountTradeRedirect />} />
        <Route element={<LayoutResolver setCurrentPath={setCurrentPath} />}>
          <Route path='login' element={<LoginPage />} />
          <Route path='login/2fa' element={<TwoFactorAuthPage />} />
          <Route path='login/2fa/expired' element={<TwoFactorAuthErrorPage />} />
          <Route path='blocked' element={<BlockedPage />} />
          {renderRoutes(SignupRoutes)}
          <Route path='logout' element={<LogoutPage />} />
          <Route path='users'>
            <Route path='reset-password/:email/:token' element={<ResetPasswordPage />} />
            <Route path='login' element={<Navigate to={`/login${search}`} />} />
            <Route path='verify-email/:leadId' element={<VerifyEmailRedirect />} />
            <Route path='register' element={<Navigate to={`/sign-up${search}`} />} />
            <Route path='register2/:leadId' element={<VerifyEmailRedirect />} />
            <Route path='register2' element={<Navigate to={`/sign-up${search}`} />} />
            <Route path='continue-registration/:leadId' element={<ContinueRegistrationPage />} />
          </Route>
          <Route path='forgot-password' element={<ForgotPasswordPage />} />
          <Route path='marketing-form' element={<MarketingPage />} />
          <Route path='*' element={<Navigate to={`/login${search}`} />} />
        </Route>
        <Route element={<LayoutResolver setCurrentPath={setCurrentPath} />}>
          <Route path='/2fa/confirm' element={<TwoFactorAuthConfirmPage />} />
        </Route>
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path='/trading-account-trade-redirect' element={<TradingAccountTradeRedirect />} />
      <Route path='*' element={<Loading isLoading showLoadingIcon />} />
    </Routes>
  )
}
